/*
 * KLM Instant Shim — replaces the instant.so runtime (instant-core.min.js)
 * for Instant-exported sections. Powers sliders, thumbnails, overlays/lightboxes,
 * accordions, quantity steppers, variant pickers, and AJAX add-to-cart using
 * Instant's own data attributes, with zero external dependencies and no watermark.
 */
(function () {
  'use strict';

  function initRoot(sectionRoot) {
    if (sectionRoot.__klmShimInit) return;
    sectionRoot.__klmShimInit = true;

    /* ---------------- Sliders ---------------- */
    var sliderRegistry = {};

    function makeSlider(el) {
      var wrapper = el.querySelector(':scope > .instant-slider-wrapper') || el.querySelector('.instant-slider-wrapper');
      if (!wrapper) return null;
      var api = {
        el: el,
        wrapper: wrapper,
        index: 0,
        count: wrapper.children.length,
        go: function (i) {
          if (!this.count) return;
          this.index = ((i % this.count) + this.count) % this.count;
          this.wrapper.style.transition = 'transform 0.3s ease';
          this.wrapper.style.transform = 'translateX(-' + (this.index * 100) + '%)';
          el.dispatchEvent(new CustomEvent('klm:slide', { detail: { index: this.index } }));
        },
        next: function () { this.go(this.index + 1); },
        prev: function () { this.go(this.index - 1); }
      };
      wrapper.style.display = 'flex';
      Array.prototype.forEach.call(wrapper.children, function (slide) {
        slide.style.flex = '0 0 100%';
        slide.style.minWidth = '100%';
      });
      el.style.overflow = 'hidden';
      return api;
    }

    // Main sliders
    sectionRoot.querySelectorAll('[data-instant-type="slider"]').forEach(function (el) {
      var id = el.getAttribute('data-instant-slider-id');
      var api = makeSlider(el);
      if (id && api) sliderRegistry[id] = api;
    });

    // Prev/next buttons — Instant encodes the slider id in a button class
    // like "instant-slider-<id>-button-prev"
    sectionRoot.querySelectorAll('[data-instant-type="slider-button-prev"], [data-instant-type="slider-button-next"]').forEach(function (btn) {
      var dir = btn.getAttribute('data-instant-type') === 'slider-button-prev' ? 'prev' : 'next';
      var match = (btn.className || '').match(/instant-slider-([A-Za-z0-9]+)-button-(?:prev|next)/);
      var target = match ? sliderRegistry[match[1]] : null;
      if (!target) {
        var ids = Object.keys(sliderRegistry);
        if (ids.length === 1) target = sliderRegistry[ids[0]];
      }
      if (target) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          target[dir]();
        });
      }
    });

    // Thumbnails — share data-instant-slider-id with their main slider
    sectionRoot.querySelectorAll('[data-instant-type="thumbnails"]').forEach(function (thumbsEl) {
      var id = thumbsEl.getAttribute('data-instant-slider-id');
      var main = id ? sliderRegistry[id] : null;
      if (!main) return;
      var wrapper = thumbsEl.querySelector('.instant-slider-wrapper');
      if (wrapper) {
        wrapper.style.display = 'flex';
        wrapper.style.flexWrap = 'wrap';
        wrapper.style.gap = '8px';
        Array.prototype.forEach.call(wrapper.children, function (slide) {
          slide.style.flex = '0 0 calc(16.666% - 7px)';
          slide.style.minWidth = '0';
          slide.style.cursor = 'pointer';
          slide.style.borderRadius = '6px';
          slide.style.overflow = 'hidden';
          slide.style.border = '1px solid transparent';
        });
      }
      var slides = wrapper ? Array.prototype.slice.call(wrapper.children) : [];
      function highlight(i) {
        slides.forEach(function (s, si) {
          s.style.borderColor = si === i ? '#e74233' : 'transparent';
        });
      }
      slides.forEach(function (slide, i) {
        slide.addEventListener('click', function () { main.go(i); });
      });
      main.el.addEventListener('klm:slide', function (e) { highlight(e.detail.index); });
      highlight(0);
    });

    /* ---------------- Overlays / lightboxes ---------------- */
    /* ROOT CAUSE, found 2026-08-04 after reading the engine section markup.

       The Instant export nests the lightbox overlay INSIDE the first slide of the
       main gallery slider:

         .instant-slider  (makeSlider sets overflow:hidden on this)
           .instant-slider-wrapper  (makeSlider sets transform:translateX() on this)
             .instant-slider-slide
               <button> the clickable photo
               .instant-overlay--lightbox   <- the popup lives here

       A transformed ancestor becomes the containing block for position:fixed
       descendants, so the overlay could never escape the gallery box: it was clipped
       by overflow:hidden and positioned relative to the sliding wrapper rather than
       the viewport. Symptom: the close button (mounted on document.body) appeared
       while the photo itself did not.

       Fix: on open, relocate the overlay to a host element appended to document.body.
       The host copies the section root's class list so the stylesheet rules in
       klm-description-fix.css — which are scoped .__instant.icrD2hoC3O9UXBXsd ... —
       keep matching after the move. Overlays are cached by action id because once
       moved they are no longer inside sectionRoot and would not be found again.

       Also note the export emits DUPLICATE id attributes: the overlay div and the
       slider div inside it share id="i<actionId>". findOverlay therefore matches on
       class + data-instant-type="overlay" first and only falls back to the id. */
    var KLM_CLOSE_ID = 'klm-lightbox-close';
    var KLM_HOST_ID = 'klm-lightbox-host';
    var overlayCache = {};

    function getOverlayHost() {
      var host = document.getElementById(KLM_HOST_ID);
      if (!host) {
        host = document.createElement('div');
        host.id = KLM_HOST_ID;
        host.className = sectionRoot.className;
        // Neutralise the section root's own background/layout on the host itself;
        // it exists only to keep the scoped CSS selectors matching.
        host.style.cssText = 'position:static;background:transparent;margin:0;padding:0;';
        document.body.appendChild(host);
      }
      return host;
    }

    function unmountCloseButton() {
      var existing = document.getElementById(KLM_CLOSE_ID);
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
    }

    function closeOverlay(overlay) {
      overlay.setAttribute('data-state', 'closed');
      overlay.style.display = 'none';
      document.body.style.overflow = '';
      unmountCloseButton();
    }

    function mountCloseButton(overlay) {
      unmountCloseButton();
      var btn = document.createElement('button');
      btn.id = KLM_CLOSE_ID;
      btn.type = 'button';
      btn.setAttribute('aria-label', 'Close image');
      btn.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">' +
        '<path d="M6 6 L18 18 M18 6 L6 18" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round" fill="none"></path></svg>';
      btn.style.cssText = [
        'position:fixed',
        'top:16px',
        'right:16px',
        'width:46px',
        'height:46px',
        'padding:0',
        'display:flex',
        'align-items:center',
        'justify-content:center',
        'border:2px solid #ffffff',
        'border-radius:50%',
        'background:#e74233',
        'box-shadow:0 2px 12px rgba(0,0,0,0.6)',
        'cursor:pointer',
        'z-index:2147483647',
        'transition:background 0.15s ease, transform 0.15s ease'
      ].join(';') + ';';
      btn.addEventListener('mouseenter', function () { btn.style.background = '#ff5544'; btn.style.transform = 'scale(1.06)'; });
      btn.addEventListener('mouseleave', function () { btn.style.background = '#e74233'; btn.style.transform = ''; });
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        closeOverlay(overlay);
      });
      document.body.appendChild(btn);
    }

    // Clear any close button stranded by a previous page state, and suppress
    // Instant's own close button wherever it sits in the markup. The
    // :not([data-klm-lightbox-close]) guard leaves the standard and turbo-kit
    // sections' own vanilla close button alone — that one works and is wired by
    // the section's inline script.
    unmountCloseButton();
    sectionRoot.querySelectorAll('.instant-overlay--lightbox__button-close:not([data-klm-lightbox-close])').forEach(function (old) {
      old.style.display = 'none';
    });

    sectionRoot.querySelectorAll('[data-instant-type="overlay"]').forEach(function (overlay) {
      if (!overlay.getAttribute('data-state')) overlay.setAttribute('data-state', 'closed');
      overlay.style.display = overlay.getAttribute('data-state') === 'open' ? 'flex' : 'none';
      overlay.querySelectorAll('[data-instant-type="slider"]').forEach(function (el) {
        var id = el.getAttribute('data-instant-slider-id');
        if (id && !sliderRegistry[id + '__overlay']) {
          var api = makeSlider(el);
          if (api) sliderRegistry[id + '__overlay'] = api;
        }
      });
      // Clicking the dark area around the image closes the lightbox.
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay && overlay.getAttribute('data-state') === 'open') closeOverlay(overlay);
      });
    });

    function findOverlay(actionId) {
      if (overlayCache[actionId]) return overlayCache[actionId];
      var sel = '.i' + actionId + '[data-instant-type="overlay"], [data-instant-type="overlay"].i' + actionId;
      var found = sectionRoot.querySelector(sel) ||
                  document.querySelector('#' + KLM_HOST_ID + ' ' + sel) ||
                  sectionRoot.querySelector('#i' + actionId);
      if (found) overlayCache[actionId] = found;
      return found;
    }

    sectionRoot.querySelectorAll('[data-instant-action-type="open-overlay"]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var actionId = btn.getAttribute('data-instant-action-id');
        if (!actionId) return;
        var overlay = findOverlay(actionId);
        if (!overlay) return;
        if (overlay.getAttribute('data-state') === 'open') {
          closeOverlay(overlay);
          return;
        }
        // Lift the overlay out of the clipped, transformed slider before showing it.
        var host = getOverlayHost();
        if (overlay.parentNode !== host) host.appendChild(overlay);
        overlay.setAttribute('data-state', 'open');
        overlay.style.display = 'flex';
        overlay.style.position = 'fixed';
        overlay.style.inset = '0';
        overlay.style.alignItems = 'center';
        overlay.style.justifyContent = 'center';
        overlay.style.background = 'rgba(0,0,0,0.92)';
        overlay.style.zIndex = '2000';
        document.body.style.overflow = 'hidden';
        mountCloseButton(overlay);
        var startSlide = parseInt(btn.getAttribute('data-instant-overlay-active-slide') || '0', 10);
        overlay.querySelectorAll('[data-instant-type="slider"]').forEach(function (el) {
          var id = el.getAttribute('data-instant-slider-id');
          var api = sliderRegistry[id + '__overlay'] || sliderRegistry[id];
          if (api) api.go(startSlide);
        });
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      // Queried against the document, not sectionRoot: an open overlay has been
      // relocated to the body-level host and is no longer inside the section.
      document.querySelectorAll('[data-instant-type="overlay"][data-state="open"]').forEach(function (overlay) {
        closeOverlay(overlay);
      });
    });

    /* ---------------- Accordions ---------------- */
    // Instant exports accordions as data-instant-type="accordion-container" holding
    // accordion-item elements whose open/closed visual state is driven entirely by
    // the item's data-state attribute (the exported CSS keys off it). The original
    // runtime owned the click handling; this module replaces it. Container may set
    // data-is-multi-open-enabled="false" to enforce one-open-at-a-time.
    sectionRoot.querySelectorAll('[data-instant-type="accordion-container"]').forEach(function (container) {
      var multiOpen = container.getAttribute('data-is-multi-open-enabled') === 'true';
      container.querySelectorAll('[data-instant-type="accordion-header"]').forEach(function (header) {
        if (header.__klmAccordion) return;
        header.__klmAccordion = true;
        header.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var item = header.closest('[data-instant-type="accordion-item"]');
          if (!item) return;
          var wasOpen = item.getAttribute('data-state') === 'open';
          if (!multiOpen) {
            container.querySelectorAll('[data-instant-type="accordion-item"]').forEach(function (other) {
              other.setAttribute('data-state', 'closed');
            });
          }
          item.setAttribute('data-state', wasOpen ? 'closed' : 'open');
        });
      });
    });

    /* ---------------- Per-form wiring ---------------- */
    sectionRoot.querySelectorAll('[data-instant-type="root"]').forEach(function (formRoot) {

      /* Quantity */
      var qty = formRoot.querySelector('.instant-quantity-input');
      function setQty(n) {
        if (!qty) return;
        var min = parseInt(qty.getAttribute('min') || '1', 10);
        var max = parseInt(qty.getAttribute('max') || '999', 10);
        qty.value = Math.min(max, Math.max(min, isNaN(n) ? min : n));
      }
      formRoot.querySelectorAll('[data-instant-action-type="quantity-decrement"]').forEach(function (btn) {
        btn.addEventListener('click', function (e) { e.preventDefault(); setQty(parseInt(qty && qty.value || '1', 10) - 1); });
      });
      formRoot.querySelectorAll('[data-instant-action-type="quantity-increment"]').forEach(function (btn) {
        btn.addEventListener('click', function (e) { e.preventDefault(); setQty(parseInt(qty && qty.value || '1', 10) + 1); });
      });
      if (qty) qty.addEventListener('change', function () { setQty(parseInt(qty.value || '1', 10)); });

      /* Variants */
      var variantsScript = formRoot.querySelector('script[type="application/json"][id^="variants__"]');
      var variants = [];
      try { variants = variantsScript ? JSON.parse(variantsScript.textContent) : []; } catch (err) { variants = []; }

      var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      var money;
      try { money = new Intl.NumberFormat('en-US', { style: 'currency', currency: currency }); }
      catch (err) { money = { format: function (n) { return '$' + n.toFixed(2); } }; }

      var atc = formRoot.querySelector('[data-instant-action-type="add-to-instant-cart"]');
      var priceEl = formRoot.querySelector('[data-instant-dynamic-content-source="PRICE"]');
      var pickers = Array.prototype.slice.call(formRoot.querySelectorAll('[data-instant-type="VARIANT_PICKER"]'));

      function selectedOptions() {
        return pickers.map(function (fs) {
          var checked = fs.querySelector('input:checked');
          if (checked) return checked.value;
          var select = fs.querySelector('select');
          if (select) return select.value;
          return null;
        });
      }

      function findVariant(opts) {
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          if ((opts[0] == null || v.option1 === opts[0]) && (opts[1] == null || v.option2 === opts[1]) && (opts[2] == null || v.option3 === opts[2])) return v;
        }
        return null;
      }

      function refreshDisabled(opts) {
        pickers.forEach(function (fs, fi) {
          fs.querySelectorAll('input').forEach(function (input) {
            var test = opts.slice();
            test[fi] = input.value;
            var v = findVariant(test);
            if (v && v.available) input.removeAttribute('data-instant-disabled');
            else input.setAttribute('data-instant-disabled', 'true');
          });
        });
      }

      function onChange() {
        var opts = selectedOptions();
        var variant = findVariant(opts);
        refreshDisabled(opts);
        if (!variant) return;
        if (priceEl) priceEl.textContent = money.format(variant.price / 100);
        if (atc) {
          atc.setAttribute('data-instant-action-variant-id', variant.id);
          if (atc.tagName === 'A') {
            var href = atc.getAttribute('href') || '';
            atc.setAttribute('href', href.replace(/\/\d+:(\d+)/, '/' + variant.id + ':$1'));
          }
          if (variant.available) atc.removeAttribute('data-instant-disabled');
          else atc.setAttribute('data-instant-disabled', 'true');
        }
        formRoot.dispatchEvent(new CustomEvent('klm:variant', { detail: { variant: variant } }));
      }

      pickers.forEach(function (fs) { fs.addEventListener('change', onChange); });

      /* Add to cart */
      if (atc) {
        atc.addEventListener('click', function (e) {
          e.preventDefault();
          var disabled = (atc.getAttribute('data-instant-disabled') || '').trim() === 'true';
          if (disabled || atc.__klmBusy) return;
          var id = parseInt(atc.getAttribute('data-instant-action-variant-id'), 10);
          if (!id) return;
          var quantity = qty ? parseInt(qty.value || '1', 10) : 1;
          atc.__klmBusy = true;
          atc.style.opacity = '0.7';
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: id, quantity: quantity })
          }).then(function (res) {
            if (!res.ok) return res.json().then(function (j) { throw new Error(j.description || 'Could not add to cart'); });
            window.location.href = '/cart';
          }).catch(function (err) {
            atc.__klmBusy = false;
            atc.style.opacity = '';
            alert(err.message || 'Could not add to cart. Please try again.');
          });
        });
      }
    });
  }

  function initAll() {
    document.querySelectorAll('.__instant').forEach(initRoot);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  document.addEventListener('shopify:section:load', initAll);
})();
